// components/EntityCard.tsx - Compact Grid Layout
'use client';

import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { StarFilled, EnvironmentOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Entity } from '../types';
import FeatureIcon from './FeatureIcon';
import { ENTITY_CARD_SIZES } from '../constants/imageSizes';
import { getOptimizedEntityImage, getEntityImages } from '../utils/entityImage';

interface EntityCardProps {
  entity: Entity;
  isSelected: boolean;
  onClick?: () => void;
  layout?: 'grid' | 'list';
  onAddToTrip?: (entity: Entity) => void;
  isInTrip?: boolean;
}

// Default images for different campsite types
const defaultImages = {
  beach: '/default-beach-camping.jpg',
  forest: '/default-forest-camping.jpg',
  mountain: '/default-mountain-camping.jpg',
  lake: '/default-lake-camping.jpg',
  desert: '/default-desert-camping.jpg',
  default: '/default-campsite-hero.jpg'
};

// Helper to get default image based on features/name
function getDefaultImage(entity: Entity): string {
  const name = entity.name?.toLowerCase() || '';
  const features = entity.features?.map(f => f.desc?.toLowerCase() || '').join(' ') || '';
  const combined = name + ' ' + features;
  
  if (combined.includes('beach') || combined.includes('coast')) return defaultImages.beach;
  if (combined.includes('forest') || combined.includes('bush')) return defaultImages.forest;
  if (combined.includes('mountain') || combined.includes('alpine')) return defaultImages.mountain;
  if (combined.includes('lake') || combined.includes('river')) return defaultImages.lake;
  if (combined.includes('desert') || combined.includes('outback')) return defaultImages.desert;
  
  return defaultImages.default;
}

const EntityCard: React.FC<EntityCardProps> = ({ 
  entity, 
  isSelected, 
  onClick,
  layout = 'grid',
  onAddToTrip,
  isInTrip = false,
}) => {
  const safeName = entity.name || 'Australian Campsites';
  
  // Create SEO-friendly slug from entity name
  const slug = safeName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '')      // Remove leading/trailing hyphens
    .replace(/-+/g, '-');          // Replace multiple hyphens with single
  
  const entityUrl = `/apps/campsite-explorer/camp-sites/${encodeURIComponent(slug)}`;

  const [imageError, setImageError] = useState(false);

  const features = useMemo(() => ({
    amenities: entity.features?.filter(f => f.feature_type === 'TEXT') || [],
    icons: entity.features?.filter(f => f.feature_type === 'ICON') || [],
  }), [entity.features]);

  const fallbackImage = useMemo(() => getDefaultImage(entity), [entity]);

  const primaryImage = useMemo(() => {
    if (imageError) {
      return fallbackImage;
    }
    return getOptimizedEntityImage(entity, {
      width: 720,
      quality: 78,
      fallback: fallbackImage,
    });
  }, [entity, imageError, fallbackImage]);

  const allImages = useMemo(() => {
    if (imageError) return [fallbackImage];
    return getEntityImages(entity, {
      mainWidth: 480,
      thumbWidth: 240,
      quality: 75,
      fallback: fallbackImage,
    });
  }, [entity, imageError, fallbackImage]);

  const totalGalleryCount = entity.gallery_images?.length ?? 0;
  const hasMosaic = allImages.length >= 2;

  // Get location info
  const location = useMemo(() => {
    if (entity.addresses && entity.addresses[0]) {
      const addr = entity.addresses[0];
      return `${addr.town || ''}, ${addr.state || ''}`.replace(/^, |, $/, '');
    }
    return 'Australia';
  }, [entity.addresses]);

  // Get key features for display (limit to 3 for compact grid)
  const keyFeatures = useMemo(() => {
    const priorityFeatures = [
      'Free Overnight Camp', 'Free Camping', 'Power', 'Powered Sites', 
      'Toilets', 'Showers', 'Pets Welcome', 'Pet Friendly', 'Drinking Water',
      'WiFi Internet', 'Swimming Pool', 'Cabins'
    ];
    
    const priority = features.icons.filter(f => 
      priorityFeatures.some(pf => f.desc?.toLowerCase().includes(pf.toLowerCase()))
    );
    
    const remaining = features.icons.filter(f => 
      !priorityFeatures.some(pf => f.desc?.toLowerCase().includes(pf.toLowerCase()))
    );
    
    return [...priority, ...remaining].slice(0, 3);
  }, [features.icons]);

  // Determine if campsite is free
  const isFree = useMemo(() => 
    entity.features?.some(f => 
      f.desc?.toLowerCase().includes('free') || 
      f.desc?.toLowerCase().includes('no cost')
    ), [entity.features]);

  // Get campsite type
  const campsiteType = useMemo(() => {
    if (isFree) return 'Free Camping';
    if (entity.features?.some(f => f.desc?.toLowerCase().includes('caravan'))) return 'Caravan Park';
    if (entity.features?.some(f => f.desc?.toLowerCase().includes('cabin'))) return 'Holiday Park';
    return 'Campsite';
  }, [entity.features, isFree]);

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  // COMPACT GRID LAYOUT
  const CardContent = () => (
    <article
      className={`group bg-white rounded-xl border overflow-hidden transition-all duration-200 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 ${
        isSelected ? 'ring-2 ring-blue-500 shadow-md' : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onClick ? handleCardClick : undefined}
    >
      {/* Image area — shorter on mobile (full-width cards) to reduce scroll */}
      <div className="relative w-full aspect-[16/9] sm:aspect-[4/3] overflow-hidden bg-gray-100">
        {hasMosaic ? (
          /* ── Mosaic grid ──────────────────────────────────── */
          <div className="grid h-full w-full grid-cols-[1fr_0.45fr] gap-0.5">
            {/* Main image */}
            <div className="relative overflow-hidden">
              <Image
                src={allImages[0]}
                alt={`${safeName} - ${campsiteType} in ${location}`}
                fill
                className="object-cover transition-transform duration-300 group-hover:scale-105"
                sizes="(max-width: 640px) 55vw, 180px"
                unoptimized
                onError={handleImageError}
              />
            </div>
            {/* Thumbnail column */}
            <div className={`grid gap-0.5 ${allImages.length >= 3 ? 'grid-rows-2' : ''}`}>
              <div className="relative overflow-hidden">
                <Image
                  src={allImages[1]}
                  alt={`${safeName} photo 2`}
                  fill
                  className="object-cover"
                  sizes="(max-width: 640px) 25vw, 80px"
                  unoptimized
                />
              </div>
              {allImages.length >= 3 && (
                <div className="relative overflow-hidden">
                  <Image
                    src={allImages[2]}
                    alt={`${safeName} photo 3`}
                    fill
                    className="object-cover"
                    sizes="(max-width: 640px) 25vw, 80px"
                    unoptimized
                  />
                  {totalGalleryCount > 3 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="text-[11px] font-bold text-white">
                        +{totalGalleryCount - 3}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ── Single image (unchanged) ─────────────────────── */
          <Image
            src={primaryImage}
            alt={`${safeName} - ${campsiteType} in ${location}`}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes={ENTITY_CARD_SIZES}
            unoptimized
            onError={handleImageError}
          />
        )}

        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

        {/* Open in new tab */}
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (typeof window !== 'undefined') {
              window.open(entityUrl, '_blank', 'noopener,noreferrer');
            }
          }}
          className="absolute top-2 left-2 inline-flex items-center justify-center rounded-full bg-white/90 text-gray-700 hover:bg-blue-600 hover:text-white transition-colors w-8 h-8 shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          aria-label={`Open ${safeName} in a new tab`}
        >
          <ArrowRightOutlined style={{ fontSize: '12px' }} />
        </button>

        {onAddToTrip && (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onAddToTrip(entity);
            }}
            className={`absolute top-2 right-2 px-2.5 py-1.5 text-[11px] font-semibold rounded-full shadow-sm transition-all ${
              isInTrip
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-white/90 text-gray-700 hover:bg-blue-600 hover:text-white backdrop-blur-sm'
            }`}
          >
            {isInTrip ? 'Added' : '+ Trip'}
          </button>
        )}

        {/* Premium badge */}
        {entity.is_premium && (
          <div className="absolute top-2 left-2">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-amber-900 bg-amber-400/90 shadow-sm backdrop-blur-sm">
              PREMIUM
            </span>
          </div>
        )}

        {/* Free camping badge */}
        {isFree && (
          <div className="absolute bottom-2 left-2">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-green-600/90 shadow-sm backdrop-blur-sm">
              FREE
            </span>
          </div>
        )}

        {/* Rating badge */}
        {entity.rating && !isNaN(Number(entity.rating)) && Number(entity.rating) > 0 && (
          <div className="absolute bottom-2 right-2">
            <span className="bg-white/90 text-gray-900 px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center shadow-sm backdrop-blur-sm">
              <StarFilled className="mr-0.5" style={{ fontSize: '9px', color: '#f59e0b' }} />
              {entity.rating}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-2.5">
        <h3 className="font-bold text-gray-900 text-xs mb-1 line-clamp-2 leading-tight group-hover:text-blue-700 transition-colors">
          {safeName}
        </h3>

        <div className="flex items-center text-[10px] text-gray-500 mb-1.5">
          <EnvironmentOutlined className="mr-0.5 flex-shrink-0" style={{ color: '#ef4444', fontSize: '10px' }} />
          <span className="truncate">{location}</span>
        </div>

        {keyFeatures.length > 0 && (
          <div className="flex items-center gap-1 mb-1.5">
            {keyFeatures.map(feature => (
              <div
                key={feature.id}
                className="w-5 h-5 flex items-center justify-center bg-gray-50 rounded text-gray-600 border border-gray-100"
                title={feature.desc || ''}
              >
                <span className="w-3 h-3">
                  <FeatureIcon feature={feature} />
                </span>
              </div>
            ))}
            {features.icons.length > 3 && (
              <span className="text-[10px] text-gray-400 font-medium">
                +{features.icons.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
          <span className="text-[10px] font-medium text-gray-500 truncate">
            {campsiteType}
          </span>
          {isFree ? (
            <span className="text-[10px] font-bold text-green-600">
              Free
            </span>
          ) : (
            <span className="text-[10px] text-gray-400">
              Varies
            </span>
          )}
        </div>
      </div>
    </article>
  );

  // If onClick is provided, just render the card content
  if (onClick) {
    return <CardContent />;
  }

  // Otherwise wrap in Link for SEO-friendly navigation
  return (
    <Link href={entityUrl} className="block">
      <CardContent />
    </Link>
  );
};

export default EntityCard;
